/**
 * EventRow (Web) — Reusable event row for Dashboard timeline and Events panel.
 *
 * Shows: date, title, venue, lineup chips, flyer count, status indicators.
 * Uses Convex entity data for lookups.
 */

import { useMemo } from 'react';
import {
  MapPin,
  Users,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

type EventEntity = {
  _id: string;
  name: string;
  entityType: string;
  metadata?: Record<string, unknown> | null;
};

type EntityRecord = {
  _id: string;
  name: string;
  entityType: string;
};

type EventRowProps = {
  event: EventEntity;
  /** Pass entities from parent to avoid N+1 queries per row. */
  entities?: EntityRecord[] | null;
  onClick?: () => void;
  compact?: boolean;
};

export function EventRow({ event, entities, onClick, compact = false }: EventRowProps) {

  const meta = (event.metadata ?? {}) as Record<string, unknown>;
  const lineup = (meta.lineup as string[]) ?? [];
  const venueId = meta.venueId as string | null;
  const status = (meta.status as string) ?? 'confirmed';
  const dateStr = meta.date as string | null;
  const eventDate = dateStr ? new Date(dateStr) : null;

  // Derive venue name
  const venueName = useMemo(() => {
    if (!venueId || !entities) return null;
    const v = entities.find((e) => e._id === venueId);
    return v?.name ?? null;
  }, [venueId, entities]);

  // Derive lineup names
  const lineupNames = useMemo(() => {
    if (!entities || lineup.length === 0) return [];
    return lineup
      .map((id) => entities.find((e) => e._id === id)?.name)
      .filter(Boolean) as string[];
  }, [lineup, entities]);

  const now = new Date();
  const isPast = eventDate ? eventDate < now : false;
  const daysUntil = eventDate
    ? Math.ceil((eventDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const hasNoLineup = lineupNames.length === 0 && !isPast;
  const hasNoVenue = !venueId && !isPast;
  const needsAttention = hasNoLineup || hasNoVenue;

  const statusColor = isPast
    ? 'bg-muted-foreground/30'
    : needsAttention
      ? 'bg-amber-500'
      : daysUntil !== null && daysUntil <= 7
        ? 'bg-[#25a48f]'
        : 'bg-blue-500';

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 rounded-xl border border-border bg-card hover:border-[#25a48f]/30 hover:bg-card/80 transition-all text-left group ${
        compact ? 'p-3' : 'p-4'
      } ${isPast ? 'opacity-60' : ''}`}
    >
      {/* Status dot + Date */}
      {eventDate && (
        <div className="flex flex-col items-center gap-1 flex-shrink-0 w-12">
          <div className={`w-2 h-2 rounded-full ${statusColor}`} />
          <span className={`font-bold text-[#25a48f] ${compact ? 'text-sm' : 'text-lg'}`}>
            {eventDate.getDate()}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            {eventDate.toLocaleDateString('en-US', { month: 'short' })}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {eventDate.toLocaleDateString('en-US', { weekday: 'short' })}
          </span>
        </div>
      )}

      {/* Event info */}
      <div className="flex-1 min-w-0 space-y-1.5">
        {/* Title + countdown */}
        <div className="flex items-start justify-between gap-2">
          <h4 className={`font-semibold line-clamp-1 ${compact ? 'text-sm' : 'text-base'}`}>
            {event.name}
          </h4>
          {!isPast && daysUntil !== null && daysUntil <= 7 && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                daysUntil === 0
                  ? 'bg-[#ef5228]/10 text-[#ef5228]'
                  : daysUntil === 1
                    ? 'bg-amber-500/10 text-amber-500'
                    : 'bg-[#25a48f]/10 text-[#25a48f]'
              }`}
            >
              {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
            </span>
          )}
          {isPast && (
            <CheckCircle2 className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
          )}
        </div>

        {/* Venue */}
        {venueName && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <MapPin className="w-3 h-3" />
            <span className="truncate">{venueName}</span>
          </div>
        )}

        {/* Meta row: lineup, warnings */}
        <div className="flex items-center gap-3 flex-wrap">
          {lineupNames.length > 0 ? (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Users className="w-3 h-3" />
              <span className="truncate">
                {lineupNames.length <= 2
                  ? lineupNames.join(', ')
                  : `${lineupNames.slice(0, 2).join(', ')} +${lineupNames.length - 2}`}
              </span>
            </div>
          ) : (
            !isPast && (
              <div className="flex items-center gap-1 text-xs text-amber-500">
                <Users className="w-3 h-3" />
                <span>No lineup</span>
              </div>
            )
          )}

          {!isPast && !venueId && (
            <div className="flex items-center gap-1 text-xs text-amber-500">
              <AlertTriangle className="w-3 h-3" />
              <span>Needs venue</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
